<!-- src/App.vue -->
<template>
  <v-app>
    <v-app-bar app color="primary" dark dense style="z-index: 6; padding: 0 14px">
      <div class="d-flex align-center">
        <a href="https://c-state.cawthron.org.nz" target="_blank">
          <v-img
            :src="cawLogo"
            :width="$vuetify.display.smAndDown ? 90 : 130"
            contain
            transition="scale-transition"
          />
        </a>
      </div>
      <v-spacer></v-spacer>
      <v-menu open-on-click offset-y>
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props" class="mr-2">
            <v-icon size="x-large" color="white">mdi-account-circle</v-icon>
          </v-btn>
        </template>
        <v-list>
          <!-- Add user info section when authenticated -->
          <template v-if="isAuthenticated">
            <v-list-item class="user-info pa-2">
              <div class="d-flex flex-column">
                <span class="text-subtitle-2 font-weight-medium">{{ userEmail }}</span>
                <span
                  v-if="userName && userName !== userEmail"
                  class="text-caption text-medium-emphasis"
                  >{{ userName }}</span
                >
              </div>
            </v-list-item>
            <v-divider></v-divider>
          </template>

          <v-list-item
            v-if="!isAuthenticated && !isLoading"
            @click="login"
            class="menu-item"
          >
            <div class="d-flex align-center">
              <v-icon left color="#01a8ba">mdi-login</v-icon>
              <v-list-item-title class="color-text ml-2">Login</v-list-item-title>
            </div>
          </v-list-item>
          <v-list-item v-else @click="logout" class="menu-item">
            <div class="d-flex align-center">
              <v-icon left color="#01a8ba">mdi-logout</v-icon>
              <v-list-item-title class="color-text ml-2">Logout</v-list-item-title>
            </div>
          </v-list-item>
        </v-list>
      </v-menu>
      <site-selector
        v-if="isAuthenticated"
        class="ml-4 site-selector"
        @siteSelected="onSiteSelected"
      />
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>
  </v-app>
</template>
<script>
import { useAuth0 } from "@auth0/auth0-vue";
import { useStore } from "vuex";
import { watch, onMounted, computed, ref, onUnmounted } from "vue";
import cawLogo from "@/assets/cawthron_logo.svg";
import SiteSelector from "./components/SiteSelector.vue";

export default {
  name: "App",
  components: { SiteSelector },

  setup() {
    const auth0 = useAuth0();
    const store = useStore();
    const tokenExpiry = ref(null);
    let logoutInterval = null;

    // Add computed properties for user info
    const userEmail = computed(() => auth0.user.value?.email || "");
    const userName = computed(() => auth0.user.value?.name || "");

    const login = () => {
      auth0.loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
    };

    const logoutUser = () => {
      const returnToUrl = window.location.origin;
      store.dispatch("clearAuth");
      auth0.logout({ logoutParams: { returnTo: returnToUrl } });
    };

    // Function to check if token is expired
    const isTokenExpired = () => {
      const now = Math.floor(Date.now() / 1000); // Get current time in seconds
      return tokenExpiry.value && now >= tokenExpiry.value;
    };

    // Start token expiry check
    const startTokenExpiryCheck = () => {
      logoutInterval = setInterval(() => {
        if (isTokenExpired()) {
          console.warn("Token expired, logging out...");
          logoutUser();
        }
      }, 5000); // Check every 5 seconds
    };

    // Handle authentication state changes
    watch(
      () => auth0.isAuthenticated.value,
      async (isAuthenticated) => {
        if (isAuthenticated) {
          try {
            const token = await auth0.getAccessTokenSilently({
              audience: process.env.VUE_APP_AUTH0_AUDIENCE,
              scope: "read:all",
            });
            const user = auth0.user.value;

            // Decode JWT token to get expiration time
            const decodedToken = JSON.parse(atob(token.split(".")[1]));
            tokenExpiry.value = decodedToken.exp; // Store token expiry time

            store.dispatch("setAuth", { token, user });
            startTokenExpiryCheck(); // Start checking token expiry
          } catch (error) {
            console.error("Authentication error:", error);
            store.dispatch("clearAuth");
          }
        }
      }
    );

    // Check authentication status on mount
    onMounted(async () => {
      console.log('Hello World from', window.location.origin);
      if (auth0.isAuthenticated.value) {
        try {
          const token = await auth0.getAccessTokenSilently({
            audience: process.env.VUE_APP_AUTH0_AUDIENCE,
            scope: "read:all",
          });
          const user = auth0.user.value;

          // Decode JWT token to get expiration time
          const decodedToken = JSON.parse(atob(token.split(".")[1]));
          tokenExpiry.value = decodedToken.exp; // Store token expiry time

          store.dispatch("setAuth", { token, user });
          startTokenExpiryCheck(); // Start checking token expiry
        } catch (error) {
          console.error("Authentication error:", error);
          store.dispatch("clearAuth");
        }
      }
    });

    // Stop checking when component unmounts
    onUnmounted(() => {
      if (logoutInterval) {
        clearInterval(logoutInterval);
      }
    });

    return {
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading,
      userEmail,
      userName,
      login,
      logout: logoutUser,
    };
  },

  data() {
    return {
      cawLogo,
      selectedSite: "Tasman Bay",
    };
  },

  methods: {
    onSiteSelected(site) {
      this.selectedSite = site;
      const formattedSite = site.replace(/\s+/g, "-").toLowerCase();
      this.$router.push({ path: `/${formattedSite}` });
    },
  },
};
</script>

<style scoped>
.v-list-item {
  display: flex;
  align-items: center;
}

.menu-item {
  padding-left: 12px;
}

.color-text {
  color: #01a8ba;
}

.user-info {
  background-color: #f5f5f5;
  pointer-events: none;
}
</style>
